import React from 'react';
import { Box, Button, CircularProgress, Grid, TextField, Typography } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import { Controller } from 'react-hook-form';
import type { AgentConfigurationVerifoneFieldsProps } from '../../../../types/features/storeDetails/agentConfigurationCard';
import {
  adminButtonSx, adminButtonToneSx, adminCombineSx, adminFieldLabelSx, adminFormFieldSx, adminInfoPanelSx,
} from '../../../../styles/adminComponentStyles';
import { adminColors, adminTypography } from '../../../../styles/adminDesignTokens';

const AgentConfigurationVerifoneFields: React.FC<AgentConfigurationVerifoneFieldsProps> = ({
  control,
  onRetryVerifoneLogin,
  canRetryVerifoneLogin,
  retryingVerifoneLogin,
  verifoneLoginRetryNeedsSave,
}) => {
  const disableAutocomplete = {
    autoComplete: 'off',
  };

  return (
    <Box
      sx={{ ...adminInfoPanelSx, mt: 1.5 }}
    >
      <Grid container spacing={1.5}>
        <Grid size={{ xs: 12 }}>
          <Typography sx={{ ...adminFieldLabelSx, mb: 0.5 }}>
            URL
          </Typography>
          <Controller
            name="Verifone.BaseUrl"
            control={control}
            render={({ field }) => (
              <TextField
                size="small"
                fullWidth
                placeholder="Enter URL"
                value={field.value || ''}
                onChange={(e) => field.onChange(e.target.value)}
                autoComplete="off"
                slotProps={{ htmlInput: { ...disableAutocomplete, name: 'lr-verifone-base-url' } }}
                sx={adminFormFieldSx}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography sx={{ ...adminFieldLabelSx, mb: 0.5 }}>
            Username
          </Typography>
          <Controller
            name="Verifone.User"
            control={control}
            render={({ field }) => (
              <TextField
                size="small"
                fullWidth
                placeholder="Enter username"
                value={field.value || ''}
                onChange={(e) => field.onChange(e.target.value)}
                autoComplete="off"
                slotProps={{ htmlInput: { ...disableAutocomplete, name: 'lr-verifone-user' } }}
                sx={adminFormFieldSx}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Typography sx={{ ...adminFieldLabelSx, mb: 0.5 }}>
            Password
          </Typography>
          <Controller
            name="Verifone.Password"
            control={control}
            render={({ field }) => (
              <TextField
                size="small"
                fullWidth
                type="password"
                placeholder="Enter password"
                value={field.value || ''}
                onChange={(e) => field.onChange(e.target.value)}
                autoComplete="new-password"
                slotProps={{ htmlInput: { autoComplete: 'new-password', name: 'lr-verifone-password' } }}
                sx={adminFormFieldSx}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap', gap: 1 }}>
            {verifoneLoginRetryNeedsSave ? (
              <Typography sx={{ ...adminTypography.helper, color: adminColors.mutedText }}>
                Save connection changes before retrying login.
              </Typography>
            ) : null}
            <Button
              type="button"
              variant="outlined"
              size="small"
              onClick={onRetryVerifoneLogin}
              disabled={!canRetryVerifoneLogin}
              startIcon={retryingVerifoneLogin ? <CircularProgress size={16} color="inherit" /> : <ReplayIcon />}
              sx={adminCombineSx(adminButtonSx, adminButtonToneSx.secondary)}
            >
              {retryingVerifoneLogin ? 'Retrying...' : 'Retry Login'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default React.memo(AgentConfigurationVerifoneFields);
